import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logger } from '@/utils/logger';
import { toast } from 'react-hot-toast';
import { usePartCodes, useUpdatePartCodeCell, useCreatePartCode, useDeletePartCode } from '@/hooks/usePSI';
import { ICategoryInfo, IPartCode, IPartCodeCreateRequest } from '@/types/psi';
import { useAIModel } from '@/contexts/AIModelContext';
import {
  isPCBPart,
  checkGerberAvailability,
} from '@/services/pcbService';
import PCBViewer from '@/components/bom/PCBViewer';
import AddGerberDialog from '@/components/psi/AddGerberDialog';
import {
  getPartTypes,
  PartTypeOption,
  searchDigiKeyParts,
  batchCreatePartCodes,
  DigiKeyMLCCResult,
  DigiKeyMLCCResultGroup,
  getDigiKeyPartTypes,
  DigiKeyPartType,
  getDigiKeyAIStatus,
  searchPartsOnline,
  WebSearchResult,
  WebSearchPart,
} from '@/services/bomService';
import PartCreatePipeline from '@/components/flowbutton/dialogs/PartCreatePipeline';

// Hook for detecting mobile viewport
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

// Column configuration matching the part code sheets (16 columns A-P)
const COLUMNS = [
  { key: 'part_number', label: '雲騰品號', column: 'A', width: 'w-28' },
  { key: 'vendor_name', label: '廠商名稱', column: 'B', width: 'w-32' },
  { key: 'brand', label: '廠牌', column: 'C', width: 'w-24' },
  { key: 'vendor_product', label: '廠商品名', column: 'D', width: 'w-40' },
  { key: 'vendor_spec', label: '廠商規格', column: 'E', width: 'w-48' },
  { key: 'category', label: '類別', column: 'F', width: 'w-24' },
  { key: 'dimension', label: '尺寸', column: 'G', width: 'w-24' },
  { key: 'mpq', label: 'MPQ', column: 'P', width: 'w-20' },
];

interface EditableCellProps {
  value: string;
  rowIndex: number;
  column: string;
  onUpdate: (rowIndex: number, column: string, value: string) => void;
  isUpdating: boolean;
}

const EditableCell: React.FC<EditableCellProps> = ({ value, rowIndex, column, onUpdate, isUpdating }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    // Debounce the update
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      if (newValue !== value) {
        onUpdate(rowIndex, column, newValue);
      }
    }, 1000); // 1 second debounce
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    if (localValue !== value) {
      onUpdate(rowIndex, column, localValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleBlur();
    }
    if (e.key === 'Escape') {
      setLocalValue(value);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="w-full px-2 py-1 border border-indigo-500 dark:border-indigo-400 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
      />
    );
  }

  return (
    <div
      onClick={() => setIsEditing(true)}
      className={`px-2 py-1 cursor-pointer hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded min-h-[28px] text-sm text-gray-900 dark:text-white ${
        isUpdating ? 'opacity-50' : ''
      }`}
      title="Click to edit"
    >
      {localValue || <span className="text-gray-400 dark:text-gray-500 italic">-</span>}
    </div>
  );
};

interface PartCodeDialogProps {
  category: ICategoryInfo;
  onClose: () => void;
}

const PartCodeDialog: React.FC<PartCodeDialogProps> = ({ category, onClose }) => {
  const { t } = useTranslation('psi');
  const { data: partCodes, isLoading, error, refetch } = usePartCodes(category.id);
  const updateCellMutation = useUpdatePartCodeCell();
  const createPartCodeMutation = useCreatePartCode();
  const deletePartCodeMutation = useDeletePartCode();
  const { selectedModel } = useAIModel();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const partTypeScrollRef = useRef<HTMLDivElement>(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [addFormTab, setAddFormTab] = useState<'manual' | 'digikey'>('manual');
  const [showAIAssist, setShowAIAssist] = useState(false);
  const [partTypes, setPartTypes] = useState<PartTypeOption[]>([]);
  const [partTypesLoading, setPartTypesLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [newPartCode, setNewPartCode] = useState<IPartCodeCreateRequest>({
    part_number: '',
    vendor_name: '',
    brand: '',
    vendor_product: '',
    vendor_spec: '',
    category: '',
    dimension: '',
    mpq: '',
  });

  // DigiKey search state
  const [digikeyPartTypes, setDigikeyPartTypes] = useState<DigiKeyPartType[]>([]);
  const [selectedDigikeyPartType, setSelectedDigikeyPartType] = useState<string>('');
  const [digikeySearchInput, setDigikeySearchInput] = useState('');
  const [digikeySearchResults, setDigikeySearchResults] = useState<DigiKeyMLCCResultGroup[]>([]);
  const [digikeySearching, setDigikeySearching] = useState(false);
  const [digikeySearchError, setDigikeySearchError] = useState<string | null>(null);
  const [selectedDigikeyParts, setSelectedDigikeyParts] = useState<DigiKeyMLCCResult[]>([]);
  const [batchImporting, setBatchImporting] = useState(false);
  const [batchImportResult, setBatchImportResult] = useState<{success: number; total: number; errors?: string[]} | null>(null);
  const [selectedManufacturer, setSelectedManufacturer] = useState<string>('all');

  // AI Smart Parse state
  const [useAIParse, setUseAIParse] = useState(false);
  const [aiAvailable, setAiAvailable] = useState(false);

  // AI Web Search state (fallback when DigiKey returns 0 results)
  const [webSearching, setWebSearching] = useState(false);
  const [webSearchResult, setWebSearchResult] = useState<WebSearchResult | null>(null);
  const [selectedWebParts, setSelectedWebParts] = useState<WebSearchPart[]>([]);
  const [showWebSearchConfirm, setShowWebSearchConfirm] = useState(false);
  const [hasZeroDigikeyResults, setHasZeroDigikeyResults] = useState(false);
  const [webSearchConfirmChecks, setWebSearchConfirmChecks] = useState({
    verified: false,
  });

  // List view filter state - initialize from URL query param if present
  // Default to empty string (no selection) to show all items without highlighting "All" button
  const initialType = searchParams.get('type') || '';
  const [selectedListPartType, setSelectedListPartType] = useState<string>(initialType);

  // PCB Gerber Viewer state
  const [showPCBModal, setShowPCBModal] = useState(false);
  const [pcbPartNumber, setPcbPartNumber] = useState<string | null>(null);
  const [gerberAvailability, setGerberAvailability] = useState<Record<string, boolean>>({});

  // Add Gerber Dialog state (for PCB parts without gerber files)
  const [showAddGerberDialog, setShowAddGerberDialog] = useState(false);
  const [addGerberDialogPartNumber, setAddGerberDialogPartNumber] = useState<string | null>(null);

  // PCB Gerber filter state: 'all' | 'with-gerber' | 'without-gerber'
  const [gerberFilter, setGerberFilter] = useState<'all' | 'with-gerber' | 'without-gerber'>('all');

  const isElectronicParts = category.id === 'electronic_parts';

  // Update URL when part type filter changes
  const handlePartTypeChange = useCallback((newType: string) => {
    // Toggle off if clicking the same type (deselect)
    const finalType = selectedListPartType === newType ? '' : newType;
    setSelectedListPartType(finalType);
    // Update URL query string - preserve category, update type
    const newParams = new URLSearchParams(searchParams);
    if (finalType === '') {
      newParams.delete('type');
    } else {
      newParams.set('type', finalType);
    }
    setSearchParams(newParams);
  }, [searchParams, setSearchParams, selectedListPartType]);

  // Check Gerber availability for PCB parts when partCodes are loaded
  useEffect(() => {
    if (partCodes && partCodes.length > 0) {
      const pcbPartNumbers = partCodes
        .filter((pc: IPartCode) => isPCBPart(pc.part_number))
        .map((pc: IPartCode) => pc.part_number);

      if (pcbPartNumbers.length > 0) {
        checkGerberAvailability(pcbPartNumbers).then(availability => {
          setGerberAvailability(prev => ({ ...prev, ...availability }));
        });
      }
    }
  }, [partCodes]);

  // Handle closing PCB viewer modal
  const handleClosePCBModal = () => {
    setShowPCBModal(false);
  };

  // Handle closing Add Gerber dialog
  const handleCloseAddGerberDialog = () => {
    setShowAddGerberDialog(false);
    setAddGerberDialogPartNumber(null);
  };

  // Handle Add Gerber dialog success - refresh availability after adding files
  const handleAddGerberSuccess = async () => {
    if (addGerberDialogPartNumber) {
      const availability = await checkGerberAvailability([addGerberDialogPartNumber]);
      setGerberAvailability(prev => ({ ...prev, ...availability }));
    }
  };

  // Handle PCB button click - opens viewer if gerber exists, add dialog if not
  const handlePCBButtonClick = (partNumber: string) => {
    const hasGerber = gerberAvailability[partNumber] !== false;
    if (hasGerber) {
      // Open PCB viewer
      setPcbPartNumber(partNumber);
      setShowPCBModal(true);
    } else {
      // Open Add Gerber dialog
      setAddGerberDialogPartNumber(partNumber);
      setShowAddGerberDialog(true);
    }
  };

  // Fetch part types for electronic_parts category
  useEffect(() => {
    if (isElectronicParts) {
      setPartTypesLoading(true);
      getPartTypes()
        .then(setPartTypes)
        .catch(err => logger.error(err))
        .finally(() => setPartTypesLoading(false));

      // Also fetch DigiKey part types for the search tab
      getDigiKeyPartTypes()
        .then((types) => {
          setDigikeyPartTypes(types);
          // Default to Capacitor (C) if available
          const capacitorType = types.find(t => t.part_code === 'C');
          if (capacitorType) {
            setSelectedDigikeyPartType('C');
          } else if (types.length > 0) {
            setSelectedDigikeyPartType(types[0].part_code);
          }
        })
        .catch(err => logger.error(err));

      // Fetch AI parsing status
      getDigiKeyAIStatus()
        .then((status) => {
          setAiAvailable(status.ai_enabled && status.ai_available);
        })
        .catch(err => logger.error(err));
    }
  }, [isElectronicParts]);

  // Generate next part number based on selected category/type
  const generateNextPartNumber = useCallback((typeCode: string) => {
    if (!partCodes || !typeCode) return '';

    // Find all existing parts with this type code prefix
    const prefix = `${typeCode}-`;
    const existingNumbers = partCodes
      .filter((p) => p.part_number?.startsWith(prefix))
      .map((p) => {
        const suffix = p.part_number?.substring(prefix.length);
        return parseInt(suffix, 10);
      })
      .filter((n) => !isNaN(n));

    const maxNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0;
    const nextNumber = maxNumber + 1;
    return `${typeCode}-${String(nextNumber).padStart(5, '0')}`;
  }, [partCodes]);

  // Handle category/type selection change
  const handleCategoryChange = useCallback((typeCode: string) => {
    const nextPartNumber = generateNextPartNumber(typeCode);
    setNewPartCode((prev) => ({
      ...prev,
      category: typeCode,
      part_number: nextPartNumber,
    }));
    // Clear validation error for category
    setValidationErrors((prev) => ({ ...prev, category: '' }));
  }, [generateNextPartNumber]);

  // Validate required fields
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (isElectronicParts && !newPartCode.category) {
      errors.category = '請選擇類別';
    }
    if (!newPartCode.brand?.trim()) {
      errors.brand = '廠牌為必填';
    }
    if (!newPartCode.vendor_product?.trim()) {
      errors.vendor_product = '廠商品名為必填';
    }
    if (!newPartCode.vendor_spec?.trim()) {
      errors.vendor_spec = '廠商規格為必填';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCellUpdate = useCallback(
    (rowIndex: number, column: string, value: string) => {
      updateCellMutation.mutate({
        categoryId: category.id,
        data: { row_index: rowIndex, column, value },
      });
    },
    [category.id, updateCellMutation]
  );

  const handleAddPartCode = () => {
    if (!validateForm()) {
      return;
    }

    createPartCodeMutation.mutate(
      { categoryId: category.id, data: newPartCode },
      {
        onSuccess: () => {
          setShowAddForm(false);
          setNewPartCode({
            part_number: '',
            vendor_name: '',
            brand: '',
            vendor_product: '',
            vendor_spec: '',
            category: '',
            dimension: '',
            mpq: '',
          });
          setValidationErrors({});
        },
      }
    );
  };

  const handleDelete = (rowIndex: number) => {
    if (window.confirm('Are you sure you want to delete this part code?')) {
      deletePartCodeMutation.mutate({ categoryId: category.id, rowIndex });
    }
  };

  // DigiKey search handlers
  const handleDigikeySearch = async () => {
    if (!digikeySearchInput.trim()) {
      setDigikeySearchError('Please enter part specifications');
      return;
    }

    if (!selectedDigikeyPartType) {
      setDigikeySearchError('Please select a part type');
      return;
    }

    try {
      setDigikeySearching(true);
      setDigikeySearchError(null);
      setDigikeySearchResults([]);
      setSelectedDigikeyParts([]);
      setBatchImportResult(null);
      setSelectedManufacturer('all');
      // Reset web search state
      setWebSearchResult(null);
      setSelectedWebParts([]);
      setHasZeroDigikeyResults(false);

      // Split input by newlines or commas
      const specs = digikeySearchInput
        .split(/[\n,]/)
        .map(s => s.trim())
        .filter(s => s.length > 0);

      if (specs.length === 0) {
        setDigikeySearchError('Please enter at least one specification');
        return;
      }

      // Use the selected part type for search, with optional AI parsing
      const response = await searchDigiKeyParts(selectedDigikeyPartType, specs, useAIParse);
      setDigikeySearchResults(response.results);

      // Check if all search results have zero products (use actual response, not stale state)
      const allZeroResults = response.results.length > 0 && response.results.every(r => r.products.length === 0);
      setHasZeroDigikeyResults(allZeroResults);
      if (allZeroResults) {
        setDigikeySearchError('No results found for any specification');
      }
    } catch (err) {
      setDigikeySearchError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setDigikeySearching(false);
    }
  };

  const toggleDigikeyPartSelection = (part: DigiKeyMLCCResult) => {
    setSelectedDigikeyParts(prev => {
      const isSelected = prev.some(p => p.manufacturer_part_number === part.manufacturer_part_number);
      if (isSelected) {
        return prev.filter(p => p.manufacturer_part_number !== part.manufacturer_part_number);
      } else {
        return [...prev, part];
      }
    });
  };

  const isDigikeyPartSelected = (part: DigiKeyMLCCResult) => {
    return selectedDigikeyParts.some(p => p.manufacturer_part_number === part.manufacturer_part_number);
  };

  const handleBatchImport = async () => {
    if (selectedDigikeyParts.length === 0) {
      setDigikeySearchError('Please select at least one part to import');
      return;
    }

    try {
      setBatchImporting(true);
      setDigikeySearchError(null);

      // Convert selected parts to part code format
      const partCodes = selectedDigikeyParts.map(part => ({
        part_number: '',
        vendor_name: part.manufacturer_name,
        brand: part.manufacturer_name,
        vendor_product: part.manufacturer_part_number,
        vendor_spec: part.description,
        category: selectedDigikeyPartType || 'C', // Use selected part type
        dimension: part.package_size || '',
        mpq: '1',
      }));

      const result = await batchCreatePartCodes('electronic_parts', partCodes);
      setBatchImportResult({
        success: result.success_count,
        total: result.total_count,
        errors: result.errors,
      });

      // Parse duplicate errors from backend response
      const duplicateErrors = result.errors?.filter(e => e.startsWith('duplicate:')) || [];
      const duplicateNames = duplicateErrors.map(e => e.replace('duplicate: ', '').replace(' already exists', ''));
      const duplicateCount = duplicateErrors.length;

      // Find which parts were successfully added (those not in duplicate list)
      const addedParts = selectedDigikeyParts.filter(
        p => !duplicateNames.some(dn => dn.toLowerCase() === p.manufacturer_part_number.toLowerCase())
      );

      if (result.success_count > 0) {
        // Show success toast with added parts
        const addedNames = addedParts.map(p => p.manufacturer_part_number).join(', ');
        let message = `Added ${result.success_count} part${result.success_count > 1 ? 's' : ''}:\n${addedNames}`;

        if (duplicateCount > 0) {
          const existingNames = duplicateNames.join(', ');
          message += `\n\nAlready exist (${duplicateCount}):\n${existingNames}`;
        }

        toast.success(message, {
          duration: 5000,
          style: { whiteSpace: 'pre-line', maxWidth: '400px' },
        });

        setSelectedDigikeyParts([]);
        // Refresh the part codes list
        refetch();
        // Close the add form after successful import
        setTimeout(() => {
          setShowAddForm(false);
          resetDigikeySearch();
        }, 500);
      } else if (duplicateCount > 0) {
        // All parts already exist
        const existingNames = duplicateNames.join(', ');
        toast.error(`All ${duplicateCount} part${duplicateCount > 1 ? 's' : ''} already exist:\n${existingNames}`, {
          duration: 5000,
          style: { whiteSpace: 'pre-line', maxWidth: '400px' },
        });
      }
    } catch (err) {
      setDigikeySearchError(err instanceof Error ? err.message : 'Import failed');
      toast.error(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setBatchImporting(false);
    }
  };

  const resetDigikeySearch = () => {
    setDigikeySearchInput('');
    setDigikeySearchResults([]);
    setDigikeySearchError(null);
    setSelectedDigikeyParts([]);
    setBatchImportResult(null);
    setSelectedManufacturer('all');
    setUseAIParse(false);
    // Reset web search state
    setWebSearchResult(null);
    setSelectedWebParts([]);
    setHasZeroDigikeyResults(false);
    // Reset to Capacitor if available
    const capacitorType = digikeyPartTypes.find(t => t.part_code === 'C');
    if (capacitorType) {
      setSelectedDigikeyPartType('C');
    }
  };

  // Handle AI web search when DigiKey returns no results
  const handleWebSearch = async () => {
    if (!digikeySearchInput.trim() || !selectedDigikeyPartType) {
      return;
    }

    try {
      setWebSearching(true);
      setWebSearchResult(null);
      setSelectedWebParts([]);

      const spec = digikeySearchInput.trim().split(/[\n,]/)[0]?.trim() || digikeySearchInput.trim();
      const partTypeInfo = digikeyPartTypes.find(t => t.part_code === selectedDigikeyPartType);
      const originalQuery = partTypeInfo?.digikey_category
        ? `${partTypeInfo.digikey_category} ${spec}`
        : spec;

      const response = await searchPartsOnline(
        selectedDigikeyPartType,
        spec,
        originalQuery,
        'No results found',
        selectedModel.id  // Pass the user-selected AI model
      );

      setWebSearchResult(response.result);
    } catch (err) {
      setDigikeySearchError(err instanceof Error ? err.message : 'Web search failed');
    } finally {
      setWebSearching(false);
    }
  };

  // Toggle selection of a web search part
  const toggleWebPartSelection = (part: WebSearchPart) => {
    setSelectedWebParts(prev => {
      const isSelected = prev.some(p => p.manufacturer_pn === part.manufacturer_pn);
      if (isSelected) {
        return prev.filter(p => p.manufacturer_pn !== part.manufacturer_pn);
      } else {
        return [...prev, part];
      }
    });
  };

  // Handle adding selected web search parts (requires confirmation)
  const handleAddWebSearchParts = async () => {
    if (selectedWebParts.length === 0) return;

    // Show confirmation dialog
    setShowWebSearchConfirm(true);
    setWebSearchConfirmChecks({
      verified: false,
    });
  };

  // Confirm and add web search parts to sheet
  const confirmAddWebSearchParts = async () => {
    if (!selectedDigikeyPartType) return;

    try {
      setBatchImporting(true);
      setDigikeySearchError(null);

      // Convert web search parts to part code format
      const partCodesToCreate = selectedWebParts.map(part => ({
        part_number: '',
        vendor_name: part.manufacturer,
        brand: part.manufacturer,
        vendor_product: part.manufacturer_pn,
        vendor_spec: part.description,
        category: selectedDigikeyPartType,
        dimension: part.specifications?.package || part.specifications?.size || '',
        mpq: '1',
      }));

      const result = await batchCreatePartCodes('electronic_parts', partCodesToCreate);
      setBatchImportResult({
        success: result.success_count,
        total: result.total_count,
        errors: result.errors,
      });

      if (result.success_count > 0) {
        const addedNames = selectedWebParts.slice(0, result.success_count).map(p => p.manufacturer_pn).join(', ');
        toast.success(`Added ${result.success_count} AI-suggested part${result.success_count > 1 ? 's' : ''}:\n${addedNames}`, {
          duration: 5000,
          style: { whiteSpace: 'pre-line', maxWidth: '400px' },
        });

        setSelectedWebParts([]);
        setWebSearchResult(null);
        setShowWebSearchConfirm(false);
        refetch();
      } else {
        toast.error('Failed to add parts. They may already exist.');
      }
    } catch (err) {
      setDigikeySearchError(err instanceof Error ? err.message : 'Import failed');
      toast.error(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setBatchImporting(false);
      setShowWebSearchConfirm(false);
    }
  };

  // Get the selected DigiKey part type info
  const getSelectedPartTypeInfo = useCallback(() => {
    return digikeyPartTypes.find(t => t.part_code === selectedDigikeyPartType);
  }, [digikeyPartTypes, selectedDigikeyPartType]);

  // Extract unique manufacturers from search results
  const getUniqueManufacturers = useCallback(() => {
    const manufacturers = new Set<string>();
    digikeySearchResults.forEach(group => {
      group.products.forEach(product => {
        if (product.manufacturer_name) {
          manufacturers.add(product.manufacturer_name);
        }
      });
    });
    return Array.from(manufacturers).sort();
  }, [digikeySearchResults]);

  // Filter results by selected manufacturer
  const getFilteredResults = useCallback(() => {
    if (selectedManufacturer === 'all') {
      return digikeySearchResults;
    }
    return digikeySearchResults.map(group => ({
      ...group,
      products: group.products.filter(p => p.manufacturer_name === selectedManufacturer),
    })).filter(group => group.products.length > 0);
  }, [digikeySearchResults, selectedManufacturer]);

  // Get total product count for a manufacturer
  const getManufacturerCount = useCallback((manufacturer: string) => {
    if (manufacturer === 'all') {
      return digikeySearchResults.reduce((acc, group) => acc + group.products.length, 0);
    }
    return digikeySearchResults.reduce((acc, group) =>
      acc + group.products.filter(p => p.manufacturer_name === manufacturer).length, 0);
  }, [digikeySearchResults]);

  // Get unique part types from the part codes list (for list view filtering)
  const getUniquePartTypesFromList = useCallback(() => {
    if (!partCodes || !isElectronicParts) return [];
    const types = new Set<string>();
    partCodes.forEach((pc: IPartCode) => {
      if (pc.category) {
        types.add(pc.category);
      }
    });
    return Array.from(types).sort();
  }, [partCodes, isElectronicParts]);

  // Get part type label from partTypes
  const getPartTypeLabel = useCallback((typeCode: string) => {
    const pt = partTypes.find(p => p.part_code === typeCode);
    return pt ? `${typeCode} - ${pt.part_type_zh}` : typeCode;
  }, [partTypes]);

  // Filter part codes by selected part type and gerber availability
  const getFilteredPartCodes = useCallback(() => {
    if (!partCodes) return [];

    let filtered = partCodes;

    // Filter by part type (empty string means no filter = show all)
    if (selectedListPartType !== '') {
      filtered = filtered.filter((pc: IPartCode) => pc.category === selectedListPartType);
    }

    // Filter by gerber availability (only applies to PCB parts)
    if (gerberFilter !== 'all') {
      filtered = filtered.filter((pc: IPartCode) => {
        if (!isPCBPart(pc.part_number)) return true; // Non-PCB parts always pass
        const hasGerber = gerberAvailability[pc.part_number] !== false;
        return gerberFilter === 'with-gerber' ? hasGerber : !hasGerber;
      });
    }

    return filtered;
  }, [partCodes, selectedListPartType, gerberFilter, gerberAvailability]);

  // Get count of parts for a specific type
  const getPartTypeCount = useCallback((typeCode: string) => {
    if (!partCodes) return 0;
    if (typeCode === 'all') return partCodes.length;
    return partCodes.filter((pc: IPartCode) => pc.category === typeCode).length;
  }, [partCodes]);

  // Close dialog on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden m-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600">
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            <h2 className="text-xl font-bold text-white">{category.name}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {selectedListPartType === ''
                ? `${partCodes?.length || 0} items in ${category.name}`
                : `${getFilteredPartCodes().length} of ${partCodes?.length || 0} items (${getPartTypeLabel(selectedListPartType)})`
              }
            </div>
            <button
              onClick={() => {
                if (showAddForm) {
                  setShowAddForm(false);
                  setValidationErrors({});
                  resetDigikeySearch();
                } else {
                  setShowAddForm(true);
                }
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
                showAddForm
                  ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              <svg className={`w-4 h-4 transition-transform ${showAddForm ? 'rotate-45' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {showAddForm ? 'Close' : 'Add Part Code'}
            </button>
          </div>

          {/* Part Type Filter Tabs (for electronic_parts only) */}
          {isElectronicParts && getUniquePartTypesFromList().length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              {/* Mobile: Horizontal swipeable selector */}
              <div
                ref={partTypeScrollRef}
                className="flex gap-2 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2 -mx-2 px-2 md:flex-wrap md:overflow-visible md:gap-1 md:pb-0 md:mx-0 md:px-0"
                style={{ WebkitOverflowScrolling: 'touch' }}
              >
                {getUniquePartTypesFromList().map(typeCode => (
                  <button
                    key={typeCode}
                    onClick={() => handlePartTypeChange(typeCode)}
                    className={`snap-start flex-shrink-0 px-3 py-2 md:py-1 text-sm md:text-xs font-medium rounded-full transition-colors whitespace-nowrap min-h-[40px] md:min-h-0 ${
                      selectedListPartType === typeCode
                        ? 'bg-indigo-600 text-white shadow-md'
                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    {getPartTypeLabel(typeCode)} ({getPartTypeCount(typeCode)})
                  </button>
                ))}
              </div>
              {/* Mobile: Swipe hint */}
              {isMobile && getUniquePartTypesFromList().length > 3 && (
                <div className="flex items-center justify-center gap-1 mt-1 text-xs text-gray-400">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                  </svg>
                  <span>Swipe to see more</span>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Add Form */}
        {showAddForm && (
          <div className="px-6 py-4 border-b border-gray-200 bg-indigo-50">
            {/* Tab Navigation for electronic_parts */}
            {isElectronicParts && (
              <div className="flex items-center gap-2 mb-3">
                <button
                  onClick={() => { setAddFormTab('manual'); resetDigikeySearch(); }}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    addFormTab === 'manual'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  Manual Entry
                </button>
                <button
                  onClick={() => setAddFormTab('digikey')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                    addFormTab === 'digikey'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  DigiKey Search
                </button>
                <button
                  onClick={() => setShowAIAssist(true)}
                  className="px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white hover:from-purple-600 hover:to-indigo-700 shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  AI Assist
                </button>
              </div>
            )}

            {/* Manual Entry Form */}
            {(!isElectronicParts || addFormTab === 'manual') && (
              <>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Add New Part Code</h3>
                {isElectronicParts ? (
                  // Electronic Parts: Category dropdown first, then auto-generated part number
                  <div className="grid grid-cols-8 gap-2">
                    {/* 類別 dropdown - first/leftmost */}
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                        類別 <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={newPartCode.category || ''}
                        onChange={(e) => handleCategoryChange(e.target.value)}
                        disabled={partTypesLoading}
                        className={`w-full px-2 py-1.5 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm ${
                          validationErrors.category ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        <option value="">選擇類別</option>
                        {partTypes.map((pt) => (
                          <option key={pt.part_code} value={pt.part_code}>
                            {pt.part_code} - {pt.part_type_zh}
                          </option>
                        ))}
                      </select>
                      {validationErrors.category && (
                        <span className="text-xs text-red-500">{validationErrors.category}</span>
                      )}
                    </div>
                    {/* 雲騰品號 - auto-generated, read-only */}
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">雲騰品號</label>
                      <input
                        type="text"
                        value={newPartCode.part_number || ''}
                        readOnly
                        className="w-full px-2 py-1.5 border border-gray-300 rounded bg-gray-100 text-sm text-gray-600 cursor-not-allowed"
                        placeholder="自動產生"
                      />
                    </div>
                    {/* 廠商名稱 */}
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">廠商名稱</label>
                      <input
                        type="text"
                        value={newPartCode.vendor_name || ''}
                        onChange={(e) => setNewPartCode((prev) => ({ ...prev, vendor_name: e.target.value }))}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                        placeholder="廠商名稱"
                      />
                    </div>
                    {/* 廠牌 - required */}
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                        廠牌 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newPartCode.brand || ''}
                        onChange={(e) => {
                          setNewPartCode((prev) => ({ ...prev, brand: e.target.value }));
                          setValidationErrors((prev) => ({ ...prev, brand: '' }));
                        }}
                        className={`w-full px-2 py-1.5 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm ${
                          validationErrors.brand ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="廠牌"
                      />
                      {validationErrors.brand && (
                        <span className="text-xs text-red-500">{validationErrors.brand}</span>
                      )}
                    </div>
                    {/* 廠商品名 - required */}
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                        廠商品名 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newPartCode.vendor_product || ''}
                        onChange={(e) => {
                          setNewPartCode((prev) => ({ ...prev, vendor_product: e.target.value }));
                          setValidationErrors((prev) => ({ ...prev, vendor_product: '' }));
                        }}
                        className={`w-full px-2 py-1.5 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm ${
                          validationErrors.vendor_product ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="廠商品名"
                      />
                      {validationErrors.vendor_product && (
                        <span className="text-xs text-red-500">{validationErrors.vendor_product}</span>
                      )}
                    </div>
                    {/* 廠商規格 - required */}
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                    廠商規格 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newPartCode.vendor_spec || ''}
                    onChange={(e) => {
                      setNewPartCode((prev) => ({ ...prev, vendor_spec: e.target.value }));
                      setValidationErrors((prev) => ({ ...prev, vendor_spec: '' }));
                    }}
                    className={`w-full px-2 py-1.5 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm ${
                      validationErrors.vendor_spec ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="廠商規格"
                  />
                  {validationErrors.vendor_spec && (
                    <span className="text-xs text-red-500">{validationErrors.vendor_spec}</span>
                  )}
                </div>
                {/* 尺寸 */}
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">尺寸</label>
                  <input
                    type="text"
                    value={newPartCode.dimension || ''}
                    onChange={(e) => setNewPartCode((prev) => ({ ...prev, dimension: e.target.value }))}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    placeholder="尺寸"
                  />
                </div>
                {/* MPQ */}
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">MPQ</label>
                  <input
                    type="text"
                    value={newPartCode.mpq || ''}
                    onChange={(e) => setNewPartCode((prev) => ({ ...prev, mpq: e.target.value }))}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    placeholder="MPQ"
                  />
                </div>
              </div>
            ) : (
              // Non-electronic parts: Original form
              <div className="grid grid-cols-8 gap-2">
                {COLUMNS.map((col) => {
                  const isRequired = ['brand', 'vendor_product', 'vendor_spec'].includes(col.key);
                  return (
                    <div key={col.key}>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                        {col.label} {isRequired && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        type="text"
                        value={newPartCode[col.key as keyof IPartCodeCreateRequest] || ''}
                        onChange={(e) => {
                          setNewPartCode((prev) => ({ ...prev, [col.key]: e.target.value }));
                          if (isRequired) {
                            setValidationErrors((prev) => ({ ...prev, [col.key]: '' }));
                          }
                        }}
                        className={`w-full px-2 py-1.5 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm ${
                          validationErrors[col.key] ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder={col.label}
                      />
                      {validationErrors[col.key] && (
                        <span className="text-xs text-red-500">{validationErrors[col.key]}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex justify-end gap-2 mt-3">
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setValidationErrors({});
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 rounded-lg transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleAddPartCode}
                disabled={createPartCodeMutation.isLoading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {createPartCodeMutation.isLoading ? 'Adding...' : 'Add'}
              </button>
            </div>
              </>
            )}

            {/* DigiKey Search Tab (only for electronic_parts) */}
            {isElectronicParts && addFormTab === 'digikey' && (
              <div className="space-y-3">
                {/* Show search form when no results, or compact summary when results exist */}
                {digikeySearchResults.length === 0 ? (
                  <>
                    <p className="text-sm text-gray-600">
                      Search DigiKey for parts and batch import them to Google Sheets.
                    </p>

                    {/* Part Type Dropdown and Smart Parse Toggle */}
                    <div className="flex gap-4 items-end">
                      <div className="flex-1">
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                          Part Type / 零件類別
                        </label>
                        <select
                          value={selectedDigikeyPartType}
                          onChange={(e) => {
                            setSelectedDigikeyPartType(e.target.value);
                            setDigikeySearchResults([]);
                            setSelectedDigikeyParts([]);
                            setSelectedManufacturer('all');
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white"
                        >
                          {digikeyPartTypes.map((pt) => (
                            <option key={pt.part_code} value={pt.part_code}>
                              {pt.part_code} - {pt.part_type_zh} ({pt.part_type_en})
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Smart Parse Toggle */}
                      {aiAvailable && (
                        <div className="flex items-center gap-2 pb-2">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={useAIParse}
                              onChange={(e) => setUseAIParse(e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                            <span className="ms-2 text-sm font-medium text-gray-700 flex items-center gap-1">
                              Smart Parse
                              <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                            </span>
                          </label>
                        </div>
                      )}
                    </div>

                    {/* Smart Parse Hint */}
                    {useAIParse && (
                      <div className="text-xs text-indigo-600 bg-indigo-50 px-3 py-2 rounded-lg flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        AI will parse free-form text like "100nF ceramic cap X7R for decoupling" into structured search queries
                      </div>
                    )}

                    {/* Search Input */}
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                        {getSelectedPartTypeInfo()?.part_type_zh || 'Part'} Specifications
                      </label>
                      <textarea
                        value={digikeySearchInput}
                        onChange={(e) => setDigikeySearchInput(e.target.value)}
                        placeholder={getSelectedPartTypeInfo()?.placeholder || "Enter specifications, one per line"}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                        rows={3}
                      />
                      {getSelectedPartTypeInfo()?.format && (
                        <p className="text-xs text-gray-400 mt-1">
                          Format: {getSelectedPartTypeInfo()?.format}
                        </p>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={handleDigikeySearch}
                        disabled={digikeySearching || !digikeySearchInput.trim()}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                      >
                        {digikeySearching ? (
                          <>
                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Searching...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            Search DigiKey
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setShowAddForm(false);
                          resetDigikeySearch();
                        }}
                        className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  /* Compact search summary with return button when results are showing */
                  <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span className="text-xs font-medium text-gray-500 shrink-0">Search:</span>
                      <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded">
                        {getSelectedPartTypeInfo()?.part_code} - {getSelectedPartTypeInfo()?.part_type_zh}
                      </span>
                      <span className="text-sm text-gray-700 truncate" title={digikeySearchInput}>
                        {digikeySearchInput.split(/[\n,]/).filter(s => s.trim()).slice(0, 3).join(', ')}
                        {digikeySearchInput.split(/[\n,]/).filter(s => s.trim()).length > 3 && '...'}
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        setDigikeySearchResults([]);
                        setSelectedDigikeyParts([]);
                        setSelectedManufacturer('all');
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors shrink-0 ml-3"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                      </svg>
                      Modify Search
                    </button>
                  </div>
                )}

                {/* Error Display */}
                {digikeySearchError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    {digikeySearchError}
                  </div>
                )}

                {/* Web Search Button - shown when DigiKey returns 0 results and smart parse is enabled */}
                {hasZeroDigikeyResults &&
                 useAIParse &&
                 aiAvailable &&
                 !webSearchResult && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-amber-700 font-medium">
                          No DigiKey results found
                        </div>
                        <div className="text-sm text-amber-600">
                          Try AI-powered search to find alternative parts from manufacturer catalogs
                        </div>
                      </div>
                      <button
                        onClick={handleWebSearch}
                        disabled={webSearching}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {webSearching ? (
                          <>
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Searching...
                          </>
                        ) : (
                          <>
                            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                            </svg>
                            Search Online with AI
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Web Search Results */}
                {webSearchResult && webSearchResult.parts.length > 0 && (
                  <div className="space-y-3">
                    {/* Prompt Display (Collapsible) */}
                    {webSearchResult.prompt && (
                      <details className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <summary className="cursor-pointer text-sm font-medium text-gray-700 flex items-center gap-2">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                          </svg>
                          Prompt Sent to Claude AI
                        </summary>
                        <pre className="mt-3 p-3 bg-gray-100 rounded text-xs text-gray-700 whitespace-pre-wrap overflow-x-auto max-h-64 overflow-y-auto font-mono">
                          {webSearchResult.prompt}
                        </pre>
                      </details>
                    )}

                    <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-medium text-purple-700">
                            AI Suggested Parts
                          </h4>
                          <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-600">
                            {Math.round(webSearchResult.confidence * 100)}% confidence
                          </span>
                        </div>
                        {selectedWebParts.length > 0 && (
                          <button
                            onClick={handleAddWebSearchParts}
                            className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
                          >
                            Add {selectedWebParts.length} to Sheet
                          </button>
                        )}
                      </div>

                      {webSearchResult.ai_explanation && (
                        <p className="text-sm text-purple-600 mb-3">{webSearchResult.ai_explanation}</p>
                      )}

                      <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-3">
                        <p className="text-xs text-amber-700">
                          These parts are AI suggestions. Verify specifications before ordering.
                        </p>
                      </div>

                      <div className="space-y-2">
                        {webSearchResult.parts.map((part, idx) => (
                          <div
                            key={idx}
                            onClick={() => toggleWebPartSelection(part)}
                            className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                              selectedWebParts.some(p => p.manufacturer_pn === part.manufacturer_pn)
                                ? 'border-purple-500 bg-purple-100'
                                : 'border-gray-200 bg-white hover:border-purple-300'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-sm font-medium text-gray-900">
                                    {part.manufacturer_pn}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {part.manufacturer}
                                  </span>
                                  <span className="px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-600">
                                    {Math.round(part.confidence * 100)}%
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600 mt-1">{part.description}</p>
                                {part.estimated_price && (
                                  <p className="text-xs text-gray-500 mt-1">Est. price: {part.estimated_price}</p>
                                )}
                                {part.alternatives && part.alternatives.length > 0 && (
                                  <p className="text-xs text-gray-400 mt-1">
                                    Alternatives: {part.alternatives.join(', ')}
                                  </p>
                                )}
                              </div>
                              <div className="ml-3">
                                <input
                                  type="checkbox"
                                  checked={selectedWebParts.some(p => p.manufacturer_pn === part.manufacturer_pn)}
                                  onChange={() => {}}
                                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {webSearchResult.sources && webSearchResult.sources.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-purple-200">
                          <p className="text-xs text-purple-500">
                            Sources: {webSearchResult.sources.join(', ')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Web Search Confirmation Dialog */}
                {showWebSearchConfirm && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Confirm AI-Suggested Parts
                      </h3>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                        <p className="text-sm text-amber-700">
                          ⚠️ These parts were found via AI search, not DigiKey.
                        </p>
                      </div>
                      <div className="mb-6">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={webSearchConfirmChecks.verified}
                            onChange={(e) => setWebSearchConfirmChecks(prev => ({ ...prev, verified: e.target.checked }))}
                            className="mt-0.5 w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                          />
                          <span className="text-sm text-gray-700">
                            I have verified the specifications match my requirements
                          </span>
                        </label>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setShowWebSearchConfirm(false)}
                          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={confirmAddWebSearchParts}
                          disabled={!webSearchConfirmChecks.verified || batchImporting}
                          className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {batchImporting ? 'Adding...' : 'Add to Sheet'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Batch Import Result */}
                {batchImportResult && (
                  <div className={`p-3 rounded-lg text-sm ${batchImportResult.success > 0 ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-600'}`}>
                    <div className="font-medium">
                      Import Result: {batchImportResult.success} / {batchImportResult.total} parts added
                    </div>
                    {batchImportResult.errors && batchImportResult.errors.length > 0 && (
                      <ul className="list-disc list-inside mt-1 text-xs">
                        {batchImportResult.errors.slice(0, 3).map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                        {batchImportResult.errors.length > 3 && (
                          <li>...and {batchImportResult.errors.length - 3} more errors</li>
                        )}
                      </ul>
                    )}
                  </div>
                )}

                {/* Search Results */}
                {digikeySearchResults.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium text-gray-700">
                        Results ({selectedDigikeyParts.length} selected)
                      </h4>
                      {selectedDigikeyParts.length > 0 && (
                        <button
                          onClick={handleBatchImport}
                          disabled={batchImporting}
                          className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                        >
                          {batchImporting ? (
                            <>
                              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              Importing...
                            </>
                          ) : (
                            <>Add {selectedDigikeyParts.length} to Sheet</>
                          )}
                        </button>
                      )}
                    </div>

                    {/* Manufacturer Filter Tabs */}
                    {getUniqueManufacturers().length > 1 && (
                      <div className="flex flex-wrap gap-1 pb-2 border-b border-gray-200">
                        <button
                          onClick={() => setSelectedManufacturer('all')}
                          className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                            selectedManufacturer === 'all'
                              ? 'bg-indigo-600 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          All ({getManufacturerCount('all')})
                        </button>
                        {getUniqueManufacturers().map(manufacturer => (
                          <button
                            key={manufacturer}
                            onClick={() => setSelectedManufacturer(manufacturer)}
                            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                              selectedManufacturer === manufacturer
                                ? 'bg-indigo-600 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {manufacturer} ({getManufacturerCount(manufacturer)})
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                      {getFilteredResults().map((group, groupIndex) => (
                        <div key={groupIndex} className="border-b border-gray-200 last:border-b-0">
                          <div className="bg-gray-100 px-3 py-2 flex items-center justify-between">
                            <span className="font-medium text-sm text-gray-700">{group.input}</span>
                            <span className="text-xs text-gray-500">{group.products.length} results</span>
                          </div>
                          {group.error && (
                            <div className="px-3 py-2 text-sm text-red-500">{group.error}</div>
                          )}
                          {group.products.map((product, productIndex) => (
                            <div
                              key={productIndex}
                              onClick={() => toggleDigikeyPartSelection(product)}
                              className={`flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                                isDigikeyPartSelected(product) ? 'bg-indigo-50' : ''
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isDigikeyPartSelected(product)}
                                onChange={() => {}}
                                className="w-4 h-4 text-indigo-600 rounded border-gray-300"
                              />
                              <div className="ml-3 flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm text-gray-900">
                                    {product.manufacturer_part_number}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    ({product.manufacturer_name})
                                  </span>
                                </div>
                                <p className="text-xs text-gray-500 truncate">{product.description}</p>
                              </div>
                              <div className="ml-3 text-right">
                                <div className="text-sm font-medium text-gray-900">${product.unit_price.toFixed(4)}</div>
                                <div className="text-xs text-gray-500">{product.quantity_available.toLocaleString()} avail</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className="overflow-auto max-h-[calc(90vh-200px)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
            </div>
          ) : error ? (
            <div className="text-center text-red-600 py-8">Failed to load part codes</div>
          ) : !partCodes || partCodes.length === 0 ? (
            <div className="text-center text-gray-500 dark:text-gray-400 py-12">
              <svg className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-lg font-medium">No part codes found</p>
              <p className="text-sm">Click "Add Part Code" to create one.</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              {isMobile ? (
                <div className="p-3 space-y-3">
                  {/* PCB Gerber Filter for mobile */}
                  {partCodes?.some((pc: IPartCode) => isPCBPart(pc.part_number)) && (
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-xs text-gray-500">PCB Filter:</span>
                      <select
                        value={gerberFilter}
                        onChange={(e) => setGerberFilter(e.target.value as 'all' | 'with-gerber' | 'without-gerber')}
                        className="text-sm bg-white border border-gray-300 rounded-lg px-2 py-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="all">{t('pcb.filter.all')}</option>
                        <option value="with-gerber">{t('pcb.filter.withGerber')}</option>
                        <option value="without-gerber">{t('pcb.filter.withoutGerber')}</option>
                      </select>
                    </div>
                  )}
                  {/* Part Cards */}
                  {getFilteredPartCodes().map((partCode: IPartCode) => (
                    <div
                      key={partCode.row_index}
                      className="bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm overflow-hidden"
                    >
                      {/* Card Header */}
                      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 border-b border-gray-200 dark:border-gray-600">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400">#{partCode.row_index}</span>
                          <span className="font-semibold text-gray-900 dark:text-white text-sm">
                            {partCode.part_number || '-'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          {/* PCB Button - only for PCB parts */}
                          {isPCBPart(partCode.part_number) && (
                            <button
                              onClick={() => handlePCBButtonClick(partCode.part_number)}
                              className={`p-2 rounded-lg transition-colors ${
                                gerberAvailability[partCode.part_number] === false
                                  ? 'text-red-500 bg-red-50 active:bg-red-100'
                                  : 'text-green-600 bg-green-50 active:bg-green-100'
                              }`}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                              </svg>
                            </button>
                          )}
                          {/* Delete Button */}
                          <button
                            onClick={() => handleDelete(partCode.row_index)}
                            disabled={deletePartCodeMutation.isLoading}
                            className="p-2 text-red-600 bg-red-50 rounded-lg active:bg-red-100 transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* Card Body - Grid layout for mobile */}
                      <div className="p-4 grid grid-cols-2 gap-3">
                        {/* Brand */}
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">廠牌</label>
                          <EditableCell
                            value={partCode.brand || ''}
                            rowIndex={partCode.row_index}
                            column="C"
                            onUpdate={handleCellUpdate}
                            isUpdating={updateCellMutation.isLoading}
                          />
                        </div>
                        {/* Vendor Name */}
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">廠商名稱</label>
                          <EditableCell
                            value={partCode.vendor_name || ''}
                            rowIndex={partCode.row_index}
                            column="B"
                            onUpdate={handleCellUpdate}
                            isUpdating={updateCellMutation.isLoading}
                          />
                        </div>
                        {/* Vendor Product - Full width */}
                        <div className="col-span-2">
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">廠商品名</label>
                          <EditableCell
                            value={partCode.vendor_product || ''}
                            rowIndex={partCode.row_index}
                            column="D"
                            onUpdate={handleCellUpdate}
                            isUpdating={updateCellMutation.isLoading}
                          />
                        </div>
                        {/* Vendor Spec - Full width */}
                        <div className="col-span-2">
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">廠商規格</label>
                          <EditableCell
                            value={partCode.vendor_spec || ''}
                            rowIndex={partCode.row_index}
                            column="E"
                            onUpdate={handleCellUpdate}
                            isUpdating={updateCellMutation.isLoading}
                          />
                        </div>
                        {/* Category */}
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">類別</label>
                          <EditableCell
                            value={partCode.category || ''}
                            rowIndex={partCode.row_index}
                            column="F"
                            onUpdate={handleCellUpdate}
                            isUpdating={updateCellMutation.isLoading}
                          />
                        </div>
                        {/* Dimension */}
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">尺寸</label>
                          <EditableCell
                            value={partCode.dimension || ''}
                            rowIndex={partCode.row_index}
                            column="G"
                            onUpdate={handleCellUpdate}
                            isUpdating={updateCellMutation.isLoading}
                          />
                        </div>
                        {/* MPQ */}
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">MPQ</label>
                          <EditableCell
                            value={partCode.mpq || ''}
                            rowIndex={partCode.row_index}
                            column="P"
                            onUpdate={handleCellUpdate}
                            isUpdating={updateCellMutation.isLoading}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* Desktop Table View */
                <table className="w-full">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-12">
                        #
                      </th>
                      {COLUMNS.map((col) => (
                        <th
                          key={col.key}
                          className={`px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${col.width}`}
                        >
                          {col.label}
                        </th>
                      ))}
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32">
                        <div className="flex items-center justify-end gap-2">
                          <span>Actions</span>
                          {/* PCB Gerber Filter - only show if there are PCB parts */}
                          {partCodes?.some((pc: IPartCode) => isPCBPart(pc.part_number)) && (
                            <select
                              value={gerberFilter}
                              onChange={(e) => setGerberFilter(e.target.value as 'all' | 'with-gerber' | 'without-gerber')}
                              className="text-xs font-normal normal-case bg-white border border-gray-300 rounded px-1 py-0.5 cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              title={t('pcb.filter.title')}
                            >
                              <option value="all">{t('pcb.filter.all')}</option>
                              <option value="with-gerber">{t('pcb.filter.withGerber')}</option>
                              <option value="without-gerber">{t('pcb.filter.withoutGerber')}</option>
                            </select>
                          )}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
                    {getFilteredPartCodes().map((partCode: IPartCode) => (
                      <tr key={partCode.row_index} className="hover:bg-gray-50 dark:bg-gray-700">
                        <td className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">{partCode.row_index}</td>
                        {COLUMNS.map((col) => (
                          <td key={col.key} className={col.width}>
                            <EditableCell
                              value={partCode[col.key as keyof IPartCode] as string || ''}
                              rowIndex={partCode.row_index}
                              column={col.column}
                              onUpdate={handleCellUpdate}
                              isUpdating={updateCellMutation.isLoading}
                            />
                          </td>
                        ))}
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {/* PCB Button - only for PCB parts */}
                            {isPCBPart(partCode.part_number) && (
                              <button
                                onClick={() => handlePCBButtonClick(partCode.part_number)}
                                className={`p-1 rounded transition-colors ${
                                  gerberAvailability[partCode.part_number] === false
                                    ? 'text-red-500 hover:text-red-700 hover:bg-red-50'
                                    : 'text-green-600 hover:text-green-800 hover:bg-green-50'
                                }`}
                                title={
                                  gerberAvailability[partCode.part_number] === false
                                    ? t('pcb.buttons.addGerber')
                                    : t('pcb.buttons.previewGerber')
                                }
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                                </svg>
                              </button>
                            )}
                            {/* Delete Button */}
                            <button
                              onClick={() => handleDelete(partCode.row_index)}
                              disabled={deletePartCodeMutation.isLoading}
                              className="text-red-600 hover:text-red-800 p-1 hover:bg-red-50 rounded transition-colors"
                              title="Delete"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>

        {/* PCB Gerber Viewer Modal */}
        {pcbPartNumber && (
          <PCBViewer
            partNumber={pcbPartNumber}
            isOpen={showPCBModal}
            onClose={handleClosePCBModal}
            embedded={false}
          />
        )}

        {/* Add Gerber Dialog - for PCB parts without gerber files */}
        {addGerberDialogPartNumber && (
          <AddGerberDialog
            partNumber={addGerberDialogPartNumber}
            isOpen={showAddGerberDialog}
            onClose={handleCloseAddGerberDialog}
            onSuccess={handleAddGerberSuccess}
          />
        )}

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>

      {/* AI Assist — Part Create Pipeline dialog */}
      {showAIAssist && (
        <PartCreatePipeline
          onComplete={() => {
            setShowAIAssist(false);
            toast.success('Part code created via AI Assist');
          }}
          onCancel={() => setShowAIAssist(false)}
        />
      )}
    </div>
  );
};

export default PartCodeDialog;
